import mustache from "mustache";
import { RotacionPesajeVeterinariaResponse } from "../../../../../modulos/pesaje/ganaderia/response/RotacionPesajeVeterinariaResponse";
import { formatFechaCorta } from "../../../../../utils/fechaUtils";
import { _2d } from "../../../../../utils/numberUtils";
import { IConnectRender } from "../../../../definitions/IConnectRender";
import template from "./rotacionPesajeVeterinariaListItemTemplate.html";

mustache.parse(template);

/** Contador para ids únicos por instancia. */
let ROTACION_PESAJE_VETERINARIA_ITEM_ID = 0;

/** Acciones de una fila de evento de Pesaje / Rotación / Veterinaria. */
export type RotacionPesajeVeterinariaListItemActions = {
  /** Ver el detalle del evento. */
  onView: (evento: RotacionPesajeVeterinariaResponse) => void;
  /** Editar el evento. */
  onEdit: (evento: RotacionPesajeVeterinariaResponse) => void;
  /** Eliminar el evento. */
  onDelete: (evento: RotacionPesajeVeterinariaResponse) => void;
};

/** Fila de la lista de eventos de Pesaje / Rotación / Veterinaria
 *  (Render/Connect). Adaptativa según los flags del evento: avatar con la fecha,
 *  potrero origen (→ destino si rotó), resumen por acción y un badge-ícono por
 *  cada acción activa, más acciones ver/editar/eliminar. */
export class RotacionPesajeVeterinariaListItemRender implements IConnectRender {
  /** Id único de la instancia (para los ids del DOM). */
  private readonly _itemId: number;

  /**
   * @param _evento  Evento a mostrar.
   * @param _actions Handlers de ver/editar/eliminar.
   */
  constructor(
    private readonly _evento: RotacionPesajeVeterinariaResponse,
    private readonly _actions: RotacionPesajeVeterinariaListItemActions,
  ) {
    ROTACION_PESAJE_VETERINARIA_ITEM_ID += 1;
    this._itemId = ROTACION_PESAJE_VETERINARIA_ITEM_ID;
  }

  /** HTML de la fila. */
  render(): string {
    const e = this._evento;

    // Primario: origen (→ destino solo si el evento rotó).
    let primario = e.loteOrigenNombre;
    if (e.isRotacion && e.loteDestinoNombre) {
      primario = `${e.loteOrigenNombre} → ${e.loteDestinoNombre}`;
    }

    // Secundario: solo las partes de las acciones activas.
    const partes = [`${e.cantidadAnimales} animales`];
    if (e.isPesaje && e.pesoPromedio != null && e.pesoPromedio > 0) {
      partes.push(`prom ${_2d(e.pesoPromedio)} kg`);
    }
    if (e.isVeterinaria) {
      const n = e.procedimientoIds.length;
      partes.push(`${n} ${n === 1 ? "procedimiento" : "procedimientos"}`);
    }

    return mustache.render(template, {
      id: this._itemId,
      fecha: formatFechaCorta(e.fecha),
      primario: primario,
      secundario: partes.join(" · "),
      withRotacion: e.isRotacion,
      withPesaje: e.isPesaje,
      withVeterinaria: e.isVeterinaria,
    });
  }

  /** Cablea los 3 botones de acción. */
  connect(): void {
    this._view = document.getElementById(`rpv-view-${this._itemId}`);
    this._edit = document.getElementById(`rpv-edit-${this._itemId}`);
    this._delete = document.getElementById(`rpv-delete-${this._itemId}`);
    this._view?.addEventListener("click", this._onView);
    this._edit?.addEventListener("click", this._onEdit);
    this._delete?.addEventListener("click", this._onDelete);
  }

  /** Libera los listeners. */
  dispose(): void {
    this._view?.removeEventListener("click", this._onView);
    this._edit?.removeEventListener("click", this._onEdit);
    this._delete?.removeEventListener("click", this._onDelete);
  }

  private _view: HTMLElement | null = null;
  private _edit: HTMLElement | null = null;
  private _delete: HTMLElement | null = null;

  private _onView = (): void => this._actions.onView(this._evento);
  private _onEdit = (): void => this._actions.onEdit(this._evento);
  private _onDelete = (): void => this._actions.onDelete(this._evento);
}
